import React, { useMemo, useState } from "react";
import { InboxIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import MonthYearPicker from "./MonthYearPicker";

/* ── Status styles ──────────────────────────────────────────────────────────── */
const STATUS_STYLES = {
  approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border border-rose-200",
  pending:  "bg-amber-50 text-amber-700 border border-amber-200",
};

const STATUS_DOTS = {
  approved: "bg-emerald-500",
  rejected: "bg-rose-500",
  pending:  "bg-amber-400",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

/* ════════════════════════════════════════════════════════════════════════════ */
const LeaveHistory = ({ leaveData = [], isLoading = false, onMonthChange }) => {
  const now      = new Date();
  const todayStr = now.toISOString().slice(0, 10);

  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [selYear, setSelYear]   = useState(now.getFullYear());

  const handlePickerChange = ({ month, year }) => {
    const m = month ?? now.getMonth() + 1;
    const y = year  ?? now.getFullYear();
    setSelMonth(m);
    setSelYear(y);
    if (typeof onMonthChange === "function") onMonthChange(m, y);
  };

  const filteredData = useMemo(() => {
    const pad = (n) => String(n).padStart(2, "0");
    const monthStart = `${selYear}-${pad(selMonth)}-01`;
    const lastDay    = new Date(selYear, selMonth, 0).getDate();
    const monthEnd   = `${selYear}-${pad(selMonth)}-${lastDay}`;

    const base = (leaveData ?? []).filter((row) => {
      if (!selMonth || !selYear) return true;
      const start = row.start_date ?? "";
      const end   = row.end_date   ?? "";
      if (!start || !end) return false;
      return start <= monthEnd && end >= monthStart;
    });

    return [...base].sort((a, b) => {
      const aActive = a.start_date <= todayStr && a.end_date >= todayStr;
      const bActive = b.start_date <= todayStr && b.end_date >= todayStr;
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      return (b.start_date ?? "").localeCompare(a.start_date ?? "");
    });
  }, [leaveData, selMonth, selYear, todayStr]);

  const summary = useMemo(() => {
    const s = { approved: 0, pending: 0, rejected: 0 };
    filteredData.forEach((r) => {
      const k = (r.status ?? "").toLowerCase();
      if (s[k] !== undefined) s[k]++;
    });
    return s;
  }, [filteredData]);

  /* ── Loading ── */
  if (isLoading) return (
    <div className="flex flex-col items-center justify-center py-14 gap-3">
      <div className="w-7 h-7 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin" />
      <span className="text-sm font-medium text-slate-500">Loading leave records…</span>
    </div>
  );

  return (
    <>
      <style>{`
        .lh-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
        .lh-scroll::-webkit-scrollbar-track { background: transparent; }
        .lh-scroll::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 99px; }
      `}</style>

      <div className="flex flex-col gap-4">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <MonthYearPicker selectedMonth={selMonth} selectedYear={selYear} onChange={handlePickerChange} label="Select Month" />

          {/* Status summary chips */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: "approved", label: "Approved" },
              { key: "pending",  label: "Pending" },
              { key: "rejected", label: "Rejected" },
            ].map(({ key, label }) => (
              <div
                key={key}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-xl text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLES[key]}`}
              >
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOTS[key]}`} />
                {label} <span className="font-extrabold ml-0.5">{summary[key]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Empty state */}
        {filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-2 rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/30">
            <InboxIcon className="w-9 h-9 text-indigo-300" />
            <span className="text-sm font-medium text-slate-500">No leave records for this month.</span>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-indigo-100/60 shadow-sm shadow-indigo-100/40">
            <div className="overflow-y-auto overflow-x-auto lh-scroll max-h-[380px]">
              <table className="w-full text-sm text-left">
                {/* Mobile-first: hide less important columns on small screens */}
                <thead className="sticky top-0 z-10" style={{ background: "#f8faff" }}>
                  <tr>
                    <th className="py-3 px-4 whitespace-nowrap border-b border-indigo-100/60">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">From</span>
                    </th>
                    <th className="py-3 px-4 whitespace-nowrap border-b border-indigo-100/60">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">To</span>
                    </th>
                    <th className="py-3 px-4 whitespace-nowrap border-b border-indigo-100/60 hidden sm:table-cell">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Days</span>
                    </th>
                    <th className="py-3 px-4 whitespace-nowrap border-b border-indigo-100/60">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Leave Type</span>
                    </th>
                    <th className="py-3 px-4 whitespace-nowrap border-b border-indigo-100/60 text-center">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</span>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {filteredData.map((row, idx) => {
                    const statusKey  = (row.status ?? "").toLowerCase();
                    const badgeStyle = STATUS_STYLES[statusKey] ?? "bg-slate-100 text-slate-600 border border-slate-200";
                    const isActive   = row.start_date <= todayStr && row.end_date >= todayStr;

                    return (
                      <tr
                        key={row.leave_id ?? idx}
                        className={`border-t border-slate-100 transition-colors ${
                          isActive ? "bg-indigo-50/40 hover:bg-indigo-50/60" : "hover:bg-indigo-50/30"
                        }`}
                      >
                        {/* From */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {isActive && (
                              <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0 animate-pulse" />
                            )}
                            <span className="font-semibold text-slate-800 text-xs">{formatDate(row.start_date)}</span>
                          </div>
                        </td>
                        {/* To */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className="font-semibold text-slate-800 text-xs">{formatDate(row.end_date)}</span>
                        </td>
                        {/* Days */}
                        <td className="py-3 px-4 whitespace-nowrap hidden sm:table-cell">
                          <span className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-lg">
                            {row.total_days}d
                          </span>
                        </td>
                        {/* Leave Type */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-slate-700">
                            <CalendarDaysIcon className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0 hidden sm:block" />
                            <span className="text-xs">{row.leave_name ?? row.leave_type ?? "—"}</span>
                          </div>
                        </td>
                        {/* Status */}
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${badgeStyle}`}>
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_DOTS[statusKey] ?? "bg-slate-400"}`} />
                            {row.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-indigo-100/60 flex justify-between items-center bg-indigo-50/30">
              <span className="text-xs text-slate-400">{filteredData.length} record{filteredData.length !== 1 ? "s" : ""}</span>
              <span className="text-xs text-slate-400">
                {new Date(selYear, selMonth - 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LeaveHistory;